import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { IconHelpSquareRounded } from '@calendly/ui/components/glyphs/IconHelpSquareRounded';
import { IconMessage } from '@calendly/ui/components/glyphs/IconMessage';
import { Link } from '@calendly/ui/components/link';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextSecondary, colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';

/*
 * Figma Meetings Search + Filter → Empty states (e.g. 3910:25227 / 3910:25228):
 * 80px illustration, Body Bold 16 / body medium 14 muted, centered, spacing0_5x copy stack,
 * spacing1x before CTA, spacing1_5x from list top.
 */
const containerCss = "c15770f3";

/** Phone sticky Displaying N already owns the gap — no second hairline under the count. */
const containerFlushUnderPhoneCountCss = "cpv92ff";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const illustrationCss = "ir3vkc2";
const copyStackCss = "c1bhyutt";
const ctaSlotCss = "c13bxfm6";
const footerCss = "f1bpwtb2";
const Footer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerCss, props.className)} />;
});
const styledLinkCss = "s13iskp";
const StyledLink = forwardRef((props, ref) => <Link ref={ref} {...props} className={classNames(styledLinkCss, props.className)} />);
const nudgedHelpIconCss = "n1wdb5mh";
const NudgedHelpIcon = forwardRef((props, ref) => <UiIcon ref={ref} icon={IconHelpSquareRounded} size="small" {...props} className={classNames(nudgedHelpIconCss, props.className)} />);
const helpCenterLinkCss = "h184y0ki";
const HelpCenterLink = forwardRef((props, ref) => {
  const href = '/help';
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const underline = false;
  return <StyledLink ref={ref} {...props} href={href} target={target} rel={rel} underline={underline} className={classNames(helpCenterLinkCss, props.className)}>
      <NudgedHelpIcon />
      {I18n.t('publisher.meeting_intelligence.details.status_footer.help_center_link')}
    </StyledLink>;
});
const supportChatLinkCss = "s1q05ss0";
const SupportChatLink = forwardRef((props, ref) => {
  const href = 'https://calendly.com/help/support';
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const underline = false;
  return <StyledLink ref={ref} {...props} href={href} target={target} rel={rel} underline={underline} className={classNames(supportChatLinkCss, props.className)}>
      <UiIcon icon={IconMessage} size="small" />
      {I18n.t('publisher.meeting_intelligence.details.status_footer.chat_with_support_link')}
    </StyledLink>;
});
export const BaseState = ({
  heading,
  description,
  ctaButton,
  imgSrc,
  imgAltText,
  isEmptyFilteredList = false
}) => {
  const isPhone = useDeviceType('isPhone');
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const flushUnderPhoneCount = isPhone && isDefaultPresetToggleEnabled;
  return <Container data-testid="base-state" className={flushUnderPhoneCount ? containerFlushUnderPhoneCountCss : undefined}>
      {imgSrc && <img src={imgSrc} alt={imgAltText || ''} width={80} height={80} className={illustrationCss} />}
      <div className={copyStackCss} data-testid="base-state-copy">
        <BodyText weight="bold" size="large" color={colorTextStandard}>
          {heading}
        </BodyText>
        <BodyText size="medium" color={colorTextSecondary}>
          {description}
        </BodyText>
      </div>
      {ctaButton && <div className={ctaSlotCss}>{ctaButton}</div>}
      {!isEmptyFilteredList && <Footer>
          <HelpCenterLink />
          <SupportChatLink />
        </Footer>}
    </Container>;
};

require("./base_state.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./base_state.js");